// src/services/schoolsImport.ts
// -----------------------------------------------------------------------------
// Pure logic for the ACARA "Australian Schools List" CSV import (Manage →
// Schools → Import — docs/schools-admin-import-plan.md). Data in → plan out, no
// I/O: SchoolsImport feeds the parsed CSV + the ACARA IDs already in the portal
// in, gets back exactly which schools are NEW (offered as a tick-list), which are
// already present, and which rows are invalid.
//
// Rules (John, 9 July 2026):
//   - Dedup key = ACARA ID (verified 0 blank / 0 duplicate on the source file).
//   - A row already in the portal (by ACARA ID, INCLUDING deactivated rows) is
//     "already present" — report only, never resurrected (idempotent re-runs).
//   - An in-file duplicate ACARA ID collapses to one (first wins).
//   - A row with no ACARA ID or no name is invalid (can't be safely deduped).
//   - Status is not filtered — every valid, new row is offered.

import { parseCsvCells } from "./deliveryImport";

export interface SchoolCsvRow {
  acara_id: string;
  name: string;
  suburb: string;
  state: string;
  postcode: string;
  school_type: string;
}

// Parse the ACARA export into typed rows. The file's FIRST line is a title row
// ("Australian Schools List Search Results, …date…"); the real header is the
// next line. Header-driven (column order doesn't matter) — we scan for the row
// that contains an "ACARA ID" column and treat everything after it as data.
export function parseSchoolsCsv(text: string): { rows: SchoolCsvRow[]; error: string | null } {
  const cells = parseCsvCells(text);
  const headerIdx = cells.findIndex((r) => r.some((c) => c.trim() === "ACARA ID"));
  if (headerIdx === -1) {
    return {
      rows: [],
      error:
        'This doesn\'t look like the ACARA "Australian Schools List" export (no "ACARA ID" column found).',
    };
  }
  const header = cells[headerIdx].map((h) => h.trim());
  const idx = (name: string) => header.indexOf(name);
  if (idx("School Name") === -1) {
    return {
      rows: [],
      error: 'The export is missing the "School Name" column.',
    };
  }
  const dataRows = cells.slice(headerIdx + 1);
  if (dataRows.length === 0) return { rows: [], error: "No data rows found in the file." };
  const get = (r: string[], name: string) => {
    const j = idx(name);
    return j === -1 ? "" : (r[j] || "").trim();
  };
  const rows = dataRows.map((r) => ({
    acara_id: get(r, "ACARA ID"),
    name: get(r, "School Name"),
    suburb: get(r, "Suburb"),
    state: get(r, "State"),
    postcode: get(r, "Postcode"),
    school_type: get(r, "Type"),
  }));
  return { rows, error: null };
}

export interface PlannedSchool {
  acara_id: string;
  name: string;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  school_type: string | null;
}

export interface SchoolsImportPlan {
  // The new schools, offered to the admin as a tick-list (all ticked by default).
  newSchools: PlannedSchool[];
  // Already in the portal by ACARA ID (incl. deactivated) — report only.
  alreadyPresent: number;
  // In-file duplicate ACARA IDs collapsed away.
  duplicateInFile: number;
  // Rows with no ACARA ID or no name.
  invalidRows: number;
}

const normId = (v: string | null | undefined) => (v || "").trim();

// Classify each parsed CSV row against the ACARA IDs already in the portal.
export function planSchoolsImport(
  csvRows: SchoolCsvRow[],
  existingAcaraIds: string[]
): SchoolsImportPlan {
  const existing = new Set(existingAcaraIds.map(normId).filter(Boolean));
  const seen = new Set<string>();
  const plan: SchoolsImportPlan = {
    newSchools: [],
    alreadyPresent: 0,
    duplicateInFile: 0,
    invalidRows: 0,
  };
  for (const r of csvRows) {
    const id = normId(r.acara_id);
    const name = (r.name || "").trim();
    if (!id || !name) {
      plan.invalidRows += 1;
      continue;
    }
    if (existing.has(id)) {
      plan.alreadyPresent += 1;
      continue;
    }
    if (seen.has(id)) {
      plan.duplicateInFile += 1;
      continue;
    }
    seen.add(id);
    plan.newSchools.push({
      acara_id: id,
      name,
      suburb: r.suburb || null,
      state: r.state || null,
      postcode: r.postcode || null,
      school_type: r.school_type || null,
    });
  }
  return plan;
}
